import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { catchError, of } from 'rxjs';

import { IconName } from '../../../shared/components/icon/icon-data.generated';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { ProgressSpinnerComponent } from '../../../shared/components/progress-spinner/progress-spinner.component';
import { formatLocalDate, formatLocalTime } from '../../../shared/utils/date-time/date-time.utils';
import { AdminActionLogDetail } from '../../models/admin-action-log-detail';
import { AdminActionType } from '../../models/admin-action-type';
import { ActivityLogsService } from '../../services/activity-logs.service';
import { getErrorStatus } from '../../utils/http-error/http-error.utils';
import { toTypeBadgeClass } from '../../utils/type-badge/type-badge.utils';

/**
 * Which panel the second card shows. Derived from **object presence** (and, for the decision
 * panel, from the structural `isFollowUpRequired` flag which is true iff the decision is a
 * denial) — never from the raw action-type code, which is opaque and will gain new members.
 * `null` means there is no panel for this action, which is a valid state, not an error.
 */
type DetailPanel = 'note' | 'approval' | 'denial' | null;

const NOT_FOUND_MESSAGE =
  'This audit entry no longer exists. It may have been removed, or the link you followed is no longer valid.';
const LOAD_ERROR_MESSAGE = 'Something went wrong while loading this audit entry. Please try again.';

/**
 * The one `associatedRequestType` whose id `/request-detail/:requestId` can resolve. Mirrors the
 * API's `AdminActionRequestTypes.PortalAccess`; the other member is "Proxy Access", which lives in
 * a different table and has no detail screen. See {@link AdminActionDetail.fullNoteLink}.
 */
const PORTAL_ACCESS_REQUEST_TYPE = 'Portal Access';

@Component({
  selector: 'app-admin-action-detail',
  standalone: true,
  imports: [RouterLink, IconComponent, ProgressSpinnerComponent],
  templateUrl: './admin-action-detail.html',
  styleUrl: './admin-action-detail.scss',
})
export class AdminActionDetail implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly activityLogsService = inject(ActivityLogsService);

  readonly logId = Number(this.route.snapshot.paramMap.get('logId') ?? 0);

  readonly isLoading = signal(true);
  readonly loadError = signal<string | null>(null);
  readonly notFound = signal(false);
  readonly detail = signal<AdminActionLogDetail | null>(null);

  readonly errorTitle = computed(() =>
    this.notFound() ? 'Audit entry not found' : 'Unable to load audit entry',
  );
  readonly errorIcon = computed<IconName>(() => (this.notFound() ? 'search' : 'warning'));

  readonly patient = computed(() => this.detail()?.patient ?? null);
  readonly noteDetail = computed(() => this.detail()?.noteDetail ?? null);
  readonly decisionDetail = computed(() => this.detail()?.decisionDetail ?? null);

  /**
   * The block's reason and comment, shown in Event Details. Block actions only: a restore's
   * detail also carries the lifted block's reason, which must not read as part of the restore.
   */
  readonly blockDetail = computed(() => {
    const detail = this.detail();
    if (detail?.actionTypeId !== AdminActionType.Block || !detail.portalAccessDetail) {
      return null;
    }
    const portalAccess = detail.portalAccessDetail;
    return {
      reason: portalAccess.patientPortalAccessBlockReasonType,
      comment: portalAccess.notes?.trim() || null,
    };
  });

  readonly eventDate = computed(() => {
    const detail = this.detail();
    return detail ? formatLocalDate(detail.createdOnUtc) : '';
  });

  readonly eventTime = computed(() => {
    const detail = this.detail();
    return detail ? formatLocalTime(detail.createdOnUtc) : '';
  });

  /** Muted header subline: "{date} · {actor} ({actorType})". */
  readonly subline = computed(() => {
    const detail = this.detail();
    if (!detail) {
      return '';
    }
    return `${formatLocalDate(detail.createdOnUtc)} · ${detail.actorDisplayName} (${detail.actorType})`;
  });

  /**
   * Picks the second card. Note and decision context are mutually exclusive, and a decision is a
   * denial exactly when it requires follow-up — so the panel is chosen structurally rather than by
   * matching on the `decision` display string.
   */
  readonly panel = computed<DetailPanel>(() => {
    const detail = this.detail();
    if (!detail) {
      return null;
    }
    if (detail.noteDetail) {
      return 'note';
    }
    if (detail.decisionDetail) {
      return detail.decisionDetail.isFollowUpRequired ? 'denial' : 'approval';
    }
    return null;
  });

  readonly panelTitle = computed(() => {
    switch (this.panel()) {
      case 'note':
        return 'Note Details';
      case 'approval':
        return 'Approval Details';
      case 'denial':
        return 'Denial Details';
      default:
        return null;
    }
  });

  /**
   * Colour class for the heading chip, keyed off the chip's own label so it can never disagree
   * with the text it wraps. Uses the same palette as the audit feed's "Type" column, so an action
   * reads the same colour on the list and on this screen (deny red, add-note purple, and so on).
   */
  readonly actionBadgeClass = computed(
    () => `action-badge--${toTypeBadgeClass(this.detail()?.actionBadge ?? '')}`,
  );

  /**
   * Colour class for the decision value. Matching on the pre-composed `decision` string is
   * deliberate and is the only place this screen branches on display copy.
   */
  readonly decisionClass = computed(() => {
    const decision = this.decisionDetail()?.decision;
    if (decision === 'Approved') {
      return 'decision-value--approved';
    }
    if (decision === 'Denied') {
      return 'decision-value--denied';
    }
    return '';
  });

  /**
   * Where "view the full note" points when the preview is clipped. The note lives on the request it
   * was attached to, so we send the user to that request's own detail screen.
   *
   * Only a **portal-access** request has one. `/request-detail/:requestId` resolves its id against
   * `PatientPortalUserRequests`, but a proxy note's `associatedRequestId` is a
   * `PatientPortalProxyUserRequestId` — a different table with its own key sequence. Following it
   * would at best 404 and at worst open an unrelated patient's linking request that happens to
   * share the integer id, so the link is suppressed rather than pointed somewhere wrong. The
   * truncation notice still renders; only the anchor is withheld.
   *
   * `associatedRequestType` is display copy, which this screen otherwise deliberately avoids
   * branching on — it is currently the only discriminator the API exposes to the client. Switch to
   * a structural field (or an API-supplied link target) if one is added.
   */
  readonly fullNoteLink = computed(() => {
    const note = this.noteDetail();
    if (!note?.isTruncated || note.associatedRequestType !== PORTAL_ACCESS_REQUEST_TYPE) {
      return null;
    }
    return ['/request-detail', note.associatedRequestId];
  });

  ngOnInit(): void {
    if (!Number.isInteger(this.logId) || this.logId <= 0) {
      this.showNotFound();
      return;
    }

    this.activityLogsService
      .getAdminActionLogById(this.logId)
      .pipe(
        catchError((error: unknown) => {
          // A missing record arrives either as a 404 or as a RecordNotFound envelope (handled
          // below, as a null emission); anything else is a genuine failure.
          if (getErrorStatus(error) === 404) {
            this.showNotFound();
          } else {
            this.notFound.set(false);
            this.loadError.set(LOAD_ERROR_MESSAGE);
            this.isLoading.set(false);
          }
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((detail) => {
        if (!detail) {
          // Either the catchError above already set the state, or the envelope carried a null
          // result for an id that doesn't exist — both read as "not found".
          if (!this.loadError()) {
            this.showNotFound();
          }
          return;
        }
        this.detail.set(detail);
        this.loadError.set(null);
        this.notFound.set(false);
        this.isLoading.set(false);
      });
  }

  private showNotFound(): void {
    this.notFound.set(true);
    this.loadError.set(NOT_FOUND_MESSAGE);
    this.isLoading.set(false);
  }
}
